<template>
    <common-modal width="medium" @close="$emit('close')">
        <div class="mav">
            <div class="mav__header">
                <div class="mav__header-icon">
                    <common-icon name="calendar" size="semi-small"></common-icon>
                </div>
                <div class="mav__header-text">
                    <h3 class="mav__title">{{ $t('elements.availability.manage_modal.title', { locationName }) }}</h3>
                    <p class="mav__subtitle">{{ $t('elements.availability.manage_modal.subtitle', { locationName }) }}</p>
                </div>
            </div>

            <div class="mav__tabs">
                <button v-for="tab in tabs" :key="tab.id" type="button" :class="tabClasses(tab.id)" @click="activeTab = tab.id">
                    {{ tab.label }}
                </button>
            </div>

            <div v-if="loading" class="mav__banner mav__banner--info">
                {{ $t('elements.availability.manage_modal.banners.loading') }}
            </div>
            <div v-else-if="loadError" class="mav__banner mav__banner--warn">{{ loadError }}</div>
            <div v-if="saveError" class="mav__banner mav__banner--error">{{ saveError }}</div>

            <!-- General Settings -->
            <div v-if="activeTab === 'general'" class="mav__content">
                <div class="mav__section">
                    <label class="mav__field-label">{{ $t('elements.availability.manage_modal.general.working_days') }}</label>
                    <div class="mav__days-row">
                        <label v-for="day in allDays" :key="day.value" :class="dayCheckboxClasses(day.value)">
                            <input v-model="form.workingDays" type="checkbox" :value="day.value" class="mav__day-input" />
                            {{ day.label }}
                        </label>
                    </div>
                </div>

                <div class="mav__row">
                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.general.start_time') }}
                        </label>
                        <select v-model="form.startTime" class="mav__select">
                            <option v-for="t in timeOptions" :key="'s-' + t" :value="t">{{ formatTime(t) }}</option>
                        </select>
                    </div>
                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.general.end_time') }}
                        </label>
                        <select v-model="form.endTime" class="mav__select">
                            <option v-for="t in timeOptions" :key="'e-' + t" :value="t">{{ formatTime(t) }}</option>
                        </select>
                    </div>
                </div>

                <div class="mav__field">
                    <label class="mav__field-label">
                        {{ $t('elements.availability.manage_modal.general.slot_duration') }}
                    </label>
                    <select v-model="form.slotDuration" class="mav__select">
                        <option v-for="d in durationOptions" :key="d" :value="d">
                            {{ $t('elements.availability.manage_modal.general.minutes', { value: d }) }}
                        </option>
                    </select>
                </div>
            </div>

            <!-- Date-Specific -->
            <div v-if="activeTab === 'date-specific'" class="mav__content">
                <div class="mav__section-card">
                    <h4 class="mav__section-title">
                        <span class="mav__section-title-icon">+</span>
                        {{ $t('elements.availability.manage_modal.date_specific.section_create') }}
                    </h4>

                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.date_specific.date') }}
                        </label>
                        <input
                            v-model="dateSpecific.date"
                            type="date"
                            :min="todayIso"
                            class="mav__input"
                            placeholder="dd/mm/yyyy"
                        />
                    </div>

                    <div class="mav__section">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.date_specific.working_day_override') }}
                        </label>
                        <p class="mav__field-hint">
                            {{ $t('elements.availability.manage_modal.date_specific.working_day_override_hint') }}
                        </p>
                        <div class="mav__days-row">
                            <label
                                v-for="day in allDays"
                                :key="'ds-' + day.value"
                                :class="dayCheckboxClasses(day.value, 'dateSpecific')"
                            >
                                <input
                                    v-model="dateSpecific.workingDays"
                                    type="checkbox"
                                    :value="day.value"
                                    class="mav__day-input"
                                />
                                {{ day.label }}
                            </label>
                        </div>
                    </div>

                    <div class="mav__row">
                        <div class="mav__field">
                            <label class="mav__field-label">
                                {{ $t('elements.availability.manage_modal.general.start_time') }}
                            </label>
                            <select v-model="dateSpecific.startTime" class="mav__select">
                                <option v-for="t in timeOptions" :key="'ds-s-' + t" :value="t">{{ formatTime(t) }}</option>
                            </select>
                        </div>
                        <div class="mav__field">
                            <label class="mav__field-label">
                                {{ $t('elements.availability.manage_modal.general.end_time') }}
                            </label>
                            <select v-model="dateSpecific.endTime" class="mav__select">
                                <option v-for="t in timeOptions" :key="'ds-e-' + t" :value="t">{{ formatTime(t) }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.general.slot_duration') }}
                        </label>
                        <select v-model="dateSpecific.slotDuration" class="mav__select">
                            <option v-for="d in durationOptions" :key="'ds-d-' + d" :value="d">
                                {{ $t('elements.availability.manage_modal.general.minutes', { value: d }) }}
                            </option>
                        </select>
                    </div>

                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.date_specific.reason') }}
                        </label>
                        <input
                            v-model="dateSpecific.reason"
                            type="text"
                            class="mav__input"
                            :placeholder="$t('elements.availability.manage_modal.date_specific.reason_placeholder')"
                        />
                    </div>

                    <p v-if="dateSpecificError" class="mav__field-error">{{ dateSpecificError }}</p>

                    <button type="button" class="mav__btn mav__btn--primary mav__btn--full" @click="addDateSpecific">
                        <span class="mav__btn-icon">+</span>
                        {{ $t('elements.availability.manage_modal.date_specific.section_create') }}
                    </button>
                    <p class="mav__field-hint mav__field-hint--center">
                        Click here to add this date override, then press Save Changes to persist.
                    </p>
                </div>

                <div class="mav__section">
                    <h4 class="mav__section-title">
                        {{ $t('elements.availability.manage_modal.date_specific.current_heading') }}
                    </h4>
                    <div v-if="dateSpecificList.length === 0" class="mav__empty-state">
                        <div class="mav__empty-icon">
                            <common-icon name="info" size="medium"></common-icon>
                        </div>
                        <p class="mav__empty-text">{{ $t('elements.availability.manage_modal.date_specific.empty') }}</p>
                        <p class="mav__empty-hint">
                            {{ $t('elements.availability.manage_modal.date_specific.empty_hint') }}
                        </p>
                    </div>
                    <div v-else class="mav__list">
                        <div v-for="(item, idx) in dateSpecificList" :key="'dsl-' + idx" class="mav__list-item">
                            <div class="mav__list-info">
                                <span class="mav__list-date">{{ formatDateLabel(item.date) }}</span>
                                <span class="mav__list-detail">
                                    {{ formatTime(item.startTime) }} - {{ formatTime(item.endTime) }}
                                </span>
                                <span v-if="item.reason" class="mav__list-reason">{{ item.reason }}</span>
                            </div>
                            <button type="button" class="mav__list-remove" @click="removeDateSpecific(idx)">
                                <common-icon name="close" size="small"></common-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Time Blocks -->
            <div v-if="activeTab === 'time-blocks'" class="mav__content">
                <div class="mav__section-card">
                    <h4 class="mav__section-title">
                        <span class="mav__section-title-icon">+</span>
                        {{ $t('elements.availability.manage_modal.time_blocks.section_create') }}
                    </h4>

                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.time_blocks.block_name') }}
                        </label>
                        <input
                            v-model="timeBlock.name"
                            type="text"
                            class="mav__input"
                            :placeholder="$t('elements.availability.manage_modal.time_blocks.block_name_placeholder')"
                        />
                    </div>

                    <label class="mav__checkbox-row">
                        <input v-model="timeBlock.recurring" type="checkbox" class="mav__checkbox" />
                        <span>{{ $t('elements.availability.manage_modal.time_blocks.recurring') }}</span>
                    </label>

                    <div v-if="!timeBlock.recurring" class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.date_specific.date') }}
                        </label>
                        <input v-model="timeBlock.date" type="date" :min="todayIso" class="mav__input" placeholder="dd/mm/yyyy" />
                    </div>

                    <div v-else class="mav__section">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.time_blocks.repeat_on') }}
                        </label>
                        <p class="mav__field-hint">
                            {{ $t('elements.availability.manage_modal.time_blocks.repeat_on_hint') }}
                        </p>
                        <div class="mav__days-row">
                            <label
                                v-for="day in allDays"
                                :key="'tb-r-' + day.value"
                                :class="dayCheckboxClasses(day.value, 'timeBlock')"
                            >
                                <input
                                    type="checkbox"
                                    class="mav__day-input"
                                    :checked="timeBlock.recurringDays.includes(day.value)"
                                    @change="toggleRecurringDay(day.value)"
                                />
                                {{ day.label }}
                            </label>
                        </div>
                    </div>

                    <div class="mav__row">
                        <div class="mav__field">
                            <label class="mav__field-label">
                                {{ $t('elements.availability.manage_modal.general.start_time') }}
                            </label>
                            <select v-model="timeBlock.startTime" class="mav__select">
                                <option value="">--:-- --</option>
                                <option v-for="t in timeOptions" :key="'tb-s-' + t" :value="t">{{ formatTime(t) }}</option>
                            </select>
                        </div>
                        <div class="mav__field">
                            <label class="mav__field-label">
                                {{ $t('elements.availability.manage_modal.general.end_time') }}
                            </label>
                            <select v-model="timeBlock.endTime" class="mav__select">
                                <option value="">--:-- --</option>
                                <option v-for="t in timeOptions" :key="'tb-e-' + t" :value="t">{{ formatTime(t) }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="mav__field">
                        <label class="mav__field-label">
                            {{ $t('elements.availability.manage_modal.time_blocks.reason') }}
                        </label>
                        <input
                            v-model="timeBlock.reason"
                            type="text"
                            class="mav__input"
                            :placeholder="$t('elements.availability.manage_modal.time_blocks.reason_placeholder')"
                        />
                    </div>

                    <p v-if="timeBlockError" class="mav__field-error">{{ timeBlockError }}</p>

                    <button type="button" class="mav__btn mav__btn--primary mav__btn--full" @click="addTimeBlock">
                        <span class="mav__btn-icon">+</span>
                        {{ $t('elements.availability.manage_modal.time_blocks.section_create') }}
                    </button>
                    <p class="mav__field-hint mav__field-hint--center">
                        Click here to add this block, then press Save Changes to persist.
                    </p>
                </div>

                <div class="mav__section">
                    <h4 class="mav__section-title">
                        {{ $t('elements.availability.manage_modal.time_blocks.current_heading') }}
                    </h4>
                    <div v-if="timeBlockList.length === 0" class="mav__empty-state">
                        <div class="mav__empty-icon">
                            <common-icon name="info" size="medium"></common-icon>
                        </div>
                        <p class="mav__empty-text">
                            {{ $t('elements.availability.manage_modal.time_blocks.empty') }}
                        </p>
                    </div>
                    <div v-else class="mav__list">
                        <div v-for="(item, idx) in timeBlockList" :key="'tbl-' + idx" class="mav__list-item">
                            <div class="mav__list-info">
                                <span class="mav__list-date">
                                    <span v-if="item.name">{{ item.name }} —</span>
                                    <template v-if="item.recurring">
                                        {{
                                            $t('elements.availability.manage_modal.time_blocks.weekly_label', {
                                                days: recurringDaysLabel(item.recurringDays),
                                            })
                                        }}
                                    </template>
                                    <template v-else>{{ formatDateLabel(item.date) }}</template>
                                </span>
                                <span class="mav__list-detail">
                                    {{ formatTime(item.startTime) }} - {{ formatTime(item.endTime) }}
                                </span>
                                <span v-if="item.reason" class="mav__list-reason">{{ item.reason }}</span>
                            </div>
                            <button type="button" class="mav__list-remove" @click="removeTimeBlock(idx)">
                                <common-icon name="close" size="small"></common-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer buttons -->
            <div class="mav__footer">
                <button type="button" class="mav__btn mav__btn--secondary" @click="$emit('close')">
                    {{ $t('elements.availability.manage_modal.cancel') }}
                </button>
                <button type="button" class="mav__btn mav__btn--primary" :disabled="saving" @click="saveChanges">
                    {{ saving ? $t('elements.availability.manage_modal.saving') : $t('elements.availability.manage_modal.save') }}
                </button>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'

    export default {
        props: {
            userId: { type: [String, Number], required: true },
            practiceId: { type: [String, Number], required: true },
            practiceLocationId: { type: [String, Number], default: null },
            locationName: { type: String, default: 'Main Location' },
        },

        data() {
            return {
                activeTab: 'general',
                loading: false,
                loadError: '',
                saving: false,
                saveError: '',
                allDays: [
                    { value: 0, label: 'Sun' },
                    { value: 1, label: 'Mon' },
                    { value: 2, label: 'Tue' },
                    { value: 3, label: 'Wed' },
                    { value: 4, label: 'Thu' },
                    { value: 5, label: 'Fri' },
                    { value: 6, label: 'Sat' },
                ],
                durationOptions: [15, 30, 45, 60, 90, 120],
                existingScheduleId: undefined,
                existingStartWeekDate: undefined,
                existingFrequency: 'weekly',
                form: {
                    workingDays: [1, 2, 3, 4, 5],
                    startTime: '08:00',
                    endTime: '17:00',
                    slotDuration: 30,
                },
                dateSpecific: {
                    date: '',
                    workingDays: [1, 2, 3, 4, 5],
                    startTime: '08:00',
                    endTime: '17:00',
                    slotDuration: 30,
                    reason: '',
                },
                dateSpecificError: '',
                dateSpecificList: [],
                timeBlock: {
                    name: '',
                    recurring: false,
                    date: '',
                    recurringDays: [],
                    startTime: '',
                    endTime: '',
                    reason: '',
                },
                timeBlockError: '',
                timeBlockList: [],
            }
        },

        computed: {
            tabs() {
                return [
                    { id: 'general', label: this.$t('elements.availability.manage_modal.tabs.general') },
                    { id: 'date-specific', label: this.$t('elements.availability.manage_modal.tabs.date_specific') },
                    { id: 'time-blocks', label: this.$t('elements.availability.manage_modal.tabs.time_blocks') },
                ]
            },

            timeOptions() {
                const times = []
                for (let h = 0; h < 24; h += 1) {
                    for (let m = 0; m < 60; m += 30) {
                        const hh = String(h).padStart(2, '0')
                        const mm = String(m).padStart(2, '0')
                        times.push(`${hh}:${mm}`)
                    }
                }
                return times
            },

            todayIso() {
                const d = new Date()
                const yyyy = d.getFullYear()
                const mm = String(d.getMonth() + 1).padStart(2, '0')
                const dd = String(d.getDate()).padStart(2, '0')
                return `${yyyy}-${mm}-${dd}`
            },
        },

        methods: {
            tabClasses(tabId) {
                return {
                    mav__tab: true,
                    'mav__tab--active': this.activeTab === tabId,
                }
            },

            dayCheckboxClasses(dayVal, source) {
                let list
                if (source === 'dateSpecific') list = this.dateSpecific.workingDays
                else if (source === 'timeBlock') list = this.timeBlock.recurringDays
                else list = this.form.workingDays
                return {
                    mav__day: true,
                    'mav__day--checked': list.includes(dayVal),
                }
            },

            formatTime(t) {
                if (!t) return ''
                const [hStr, mStr] = t.split(':')
                let h = Number(hStr)
                const ampm = h >= 12 ? 'PM' : 'AM'
                if (h === 0) h = 12
                else if (h > 12) h -= 12
                return `${String(h).padStart(2, '0')}:${mStr} ${ampm}`
            },

            formatDateLabel(iso) {
                if (!iso) return ''
                const [y, m, d] = iso.split('-').map(Number)
                if (!y || !m || !d) return iso
                const date = new Date(y, m - 1, d)
                return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
            },

            recurringDaysLabel(days) {
                if (!Array.isArray(days) || !days.length) return ''
                return days
                    .slice()
                    .sort((a, b) => a - b)
                    .map((v) => (this.allDays.find((d) => d.value === v) || {}).label)
                    .filter(Boolean)
                    .join(', ')
            },

            isTimeAfter(start, end) {
                if (!start || !end) return false
                const [sh, sm] = start.split(':').map(Number)
                const [eh, em] = end.split(':').map(Number)
                return eh * 60 + em > sh * 60 + sm
            },

            toggleRecurringDay(dayVal) {
                const idx = this.timeBlock.recurringDays.indexOf(dayVal)
                if (idx === -1) this.timeBlock.recurringDays.push(dayVal)
                else this.timeBlock.recurringDays.splice(idx, 1)
            },

            loadSettings() {
                this.loading = true
                this.loadError = ''
                UsersAPI.getAvailability(this.userId, this.practiceId, this.practiceLocationId)
                    .then((res) => {
                        if (!res) return
                        // Capture identity for round-tripping on save
                        if (res.id != null) this.existingScheduleId = res.id
                        if (res.startWeekDate) this.existingStartWeekDate = res.startWeekDate
                        if (res.repeat) this.existingFrequency = res.repeat
                        if (res.appointmentLength) this.form.slotDuration = res.appointmentLength

                        // res.slots is a 7-element array (mon..sun), each entry is an array of {from, to} ranges in minutes
                        const slots = Array.isArray(res.slots) ? res.slots : null
                        if (slots && slots.length === 7) {
                            // Day-of-week mapping: positional index 0..6 (mon..sun) → JS getDay() value (1..6, 0)
                            const jsDays = [1, 2, 3, 4, 5, 6, 0]
                            const workingDays = []
                            let minStart = null
                            let maxEnd = null
                            slots.forEach((daySlots, idx) => {
                                if (daySlots && daySlots.length) {
                                    workingDays.push(jsDays[idx])
                                    daySlots.forEach((range) => {
                                        if (minStart == null || range.from < minStart) minStart = range.from
                                        if (maxEnd == null || range.to > maxEnd) maxEnd = range.to
                                    })
                                }
                            })
                            if (workingDays.length) this.form.workingDays = workingDays
                            if (minStart != null) this.form.startTime = this.minutesToHHMM(minStart)
                            if (maxEnd != null) this.form.endTime = this.minutesToHHMM(maxEnd)
                        }
                    })
                    .catch(() => {
                        this.loadError = this.$t('elements.availability.manage_modal.banners.load_error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },

            minutesToHHMM(min) {
                const h = Math.floor(min / 60)
                const m = min % 60
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
            },

            addDateSpecific() {
                this.dateSpecificError = ''
                const tT = (key) => this.$t(`elements.availability.manage_modal.date_specific.errors.${key}`)
                if (!this.dateSpecific.date) {
                    this.dateSpecificError = tT('date_required')
                    return
                }
                if (this.dateSpecific.date < this.todayIso) {
                    this.dateSpecificError = tT('date_past')
                    return
                }
                if (!this.isTimeAfter(this.dateSpecific.startTime, this.dateSpecific.endTime)) {
                    this.dateSpecificError = tT('end_before_start')
                    return
                }
                if (this.dateSpecificList.some((item) => item.date === this.dateSpecific.date)) {
                    this.dateSpecificError = tT('duplicate')
                    return
                }
                this.dateSpecificList.push({ ...this.dateSpecific })
                this.dateSpecific = {
                    date: '',
                    workingDays: [1, 2, 3, 4, 5],
                    startTime: '08:00',
                    endTime: '17:00',
                    slotDuration: 30,
                    reason: '',
                }
            },

            removeDateSpecific(idx) {
                this.dateSpecificList.splice(idx, 1)
            },

            addTimeBlock() {
                this.timeBlockError = ''
                const tT = (key) => this.$t(`elements.availability.manage_modal.time_blocks.errors.${key}`)
                if (this.timeBlock.recurring) {
                    if (!this.timeBlock.recurringDays.length) {
                        this.timeBlockError = tT('days_required')
                        return
                    }
                } else if (!this.timeBlock.date) {
                    this.timeBlockError = tT('date_required')
                    return
                } else if (this.timeBlock.date < this.todayIso) {
                    this.timeBlockError = tT('date_past')
                    return
                }
                if (!this.timeBlock.startTime || !this.timeBlock.endTime) {
                    this.timeBlockError = tT('times_required')
                    return
                }
                if (!this.isTimeAfter(this.timeBlock.startTime, this.timeBlock.endTime)) {
                    this.timeBlockError = tT('end_before_start')
                    return
                }
                this.timeBlockList.push({ ...this.timeBlock, recurringDays: [...this.timeBlock.recurringDays] })
                this.timeBlock = {
                    name: '',
                    recurring: false,
                    date: '',
                    recurringDays: [],
                    startTime: '',
                    endTime: '',
                    reason: '',
                }
            },

            removeTimeBlock(idx) {
                this.timeBlockList.splice(idx, 1)
            },

            buildPayload() {
                // Structured payload — backend can later consume `overrides` and `blocks` directly.
                // `schedule` keeps the same shape as Phase 2 for backwards compatibility with `modifySchedule`.
                return {
                    schedule: {
                        workingDays: this.form.workingDays,
                        startTime: this.form.startTime,
                        endTime: this.form.endTime,
                        appointmentLength: this.form.slotDuration,
                    },
                    practiceLocationId: this.practiceLocationId,
                    overrides: this.dateSpecificList.map((item) => ({
                        date: item.date,
                        workingDays: item.workingDays,
                        startTime: item.startTime,
                        endTime: item.endTime,
                        appointmentLength: item.slotDuration,
                        reason: item.reason || '',
                        practiceLocationId: this.practiceLocationId,
                    })),
                    blocks: this.timeBlockList.map((block) => ({
                        name: block.name || '',
                        recurring: !!block.recurring,
                        date: block.recurring ? null : block.date,
                        recurringDays: block.recurring ? [...block.recurringDays] : [],
                        startTime: block.startTime,
                        endTime: block.endTime,
                        reason: block.reason || '',
                        practiceLocationId: this.practiceLocationId,
                    })),
                }
            },

            persistBlocks(payload) {
                // Best-effort persistence via existing slot endpoint for non-recurring, dated blocks.
                // Recurring blocks are kept in the payload only — backend ingestion is the next step.
                const oneOffBlocks = payload.blocks.filter((b) => !b.recurring && b.date)
                if (!oneOffBlocks.length) return Promise.resolve()
                return Promise.all(
                    oneOffBlocks.map((block) =>
                        UsersAPI.setAvailabilitySlotUnavailable({
                            userId: this.userId,
                            date: block.date,
                            startTime: block.startTime,
                            endTime: block.endTime,
                            practiceId: this.practiceId,
                            practiceLocationId: this.practiceLocationId,
                        }),
                    ),
                )
            },

            // Convert simple modal form ({workingDays, startTime, endTime, slotDuration})
            // into the slot-range shape `ScheduleFromFormDataTransformer.transform` expects:
            //   { [practiceLocationId]: { days: [[{from,to}], ...], frequency, appointmentLength, id, startWeekDate } }
            // Day order in the transformer is [mon, tue, wed, thu, fri, sat, sun]; our workingDays use
            // JS getDay() convention (0=Sun..6=Sat), so we map accordingly.
            buildModifySchedulePayload() {
                const [sh, sm] = this.form.startTime.split(':').map(Number)
                const [eh, em] = this.form.endTime.split(':').map(Number)
                const fromMin = sh * 60 + sm
                const toMin = eh * 60 + em
                if (toMin <= fromMin) return null

                const jsDayOrder = [1, 2, 3, 4, 5, 6, 0] // mon..sun (matches transformer's ['mon'..'sun'])
                const days = jsDayOrder.map((jsDay) =>
                    this.form.workingDays.includes(jsDay) ? [{ from: fromMin, to: toMin }] : [],
                )
                const locKey = this.practiceLocationId ? String(this.practiceLocationId) : '0'
                return {
                    [locKey]: {
                        days,
                        frequency: this.existingFrequency || 'weekly',
                        appointmentLength: this.form.slotDuration,
                        id: this.existingScheduleId,
                        startWeekDate: this.existingStartWeekDate,
                    },
                }
            },

            refreshPractice() {
                // Refresh practice + member data so sidebar isAvailabilitySet flags and the
                // "set availability here" prompts update without a manual reload.
                if (this.$auth && typeof this.$auth.forceUpdatePractice === 'function') {
                    return Promise.resolve(this.$auth.forceUpdatePractice()).catch(() => {})
                }
                return Promise.resolve()
            },

            saveChanges() {
                if (!this.isTimeAfter(this.form.startTime, this.form.endTime)) {
                    this.saveError = this.$t('elements.availability.manage_modal.general_end_before_start')
                    this.activeTab = 'general'
                    return
                }

                // If user filled the date-specific form but didn't click "Create", run that validation
                // now so a past/invalid date doesn't get silently dropped on Save.
                if (this.dateSpecific.date) {
                    this.activeTab = 'date-specific'
                    this.addDateSpecific()
                    if (this.dateSpecificError) return
                }

                // Same for time-blocks: any partially-filled block must either pass validation or be cleared.
                if (
                    this.timeBlock.name ||
                    this.timeBlock.date ||
                    this.timeBlock.recurringDays.length ||
                    this.timeBlock.startTime ||
                    this.timeBlock.endTime ||
                    this.timeBlock.reason
                ) {
                    this.activeTab = 'time-blocks'
                    this.addTimeBlock()
                    if (this.timeBlockError) return
                }

                this.saving = true
                this.saveError = ''
                const payload = this.buildPayload()
                const modifyData = this.buildModifySchedulePayload()

                // Wrap in Promise.resolve so any synchronous throw from the transformer
                // surfaces as a rejected promise instead of an uncaught error that leaves
                // the modal stuck on "Saving..."
                // First-time save (POST) vs update (PUT) — schedule exists if loadSettings captured an id.
                const isFirstTime = !this.existingScheduleId
                const saveApi = isFirstTime ? UsersAPI.setSchedule : UsersAPI.modifySchedule

                Promise.resolve()
                    .then(() => {
                        if (!modifyData) {
                            throw new Error('Invalid schedule range')
                        }
                        return saveApi(this.userId, this.practiceId, modifyData)
                    })
                    .then(() => this.persistBlocks(payload))
                    .then(() => this.refreshPractice())
                    .then(() => {
                        this.saving = false
                        this.$emit('saved', payload)
                        this.$emit('close')
                    })
                    .catch((err) => {
                        this.saving = false
                        const status = err && err.response && err.response._status
                        const serverMsg =
                            err && err.response && (err.response.message || err.response.error || err.response.detail)
                        const base = this.$t('elements.availability.manage_modal.banners.save_error')
                        if (status && serverMsg) {
                            this.saveError = `${base} (${status}: ${serverMsg})`
                        } else if (status) {
                            this.saveError = `${base} (HTTP ${status})`
                        } else if (err && err.message) {
                            this.saveError = `${base} (${err.message})`
                        } else {
                            this.saveError = base
                        }
                        // eslint-disable-next-line no-console
                        console.error('[ManageAvailability] save failed:', err)
                    })
            },
        },

        created() {
            this.loadSettings()
        },
    }
</script>

<style scoped>
    .mav {
        display: flex;
        flex-direction: column;
        max-height: 80vh;
    }

    .mav__header {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 0 0 16px;
    }

    .mav__header-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background: #f3f4f6;
        border-radius: 8px;
        flex-shrink: 0;
    }

    .mav__title {
        font-size: 18px;
        font-weight: 600;
        color: #111827;
        margin: 0 0 4px;
    }

    .mav__subtitle {
        font-size: 13px;
        color: #6b7280;
        margin: 0;
        line-height: 1.4;
    }

    /* Tabs — pill inside track */
    .mav__tabs {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        background: #ffffff;
        border: 1px solid #d1d5db;
        border-radius: 10px;
        padding: 3px;
        gap: 2px;
    }

    .mav__tab {
        flex: 1 1 0;
        min-width: 0;
        height: 34px;
        background: transparent;
        border: none;
        border-radius: 7px;
        font-size: 13px;
        font-weight: 500;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.15s;
        padding: 0 8px;
        text-align: center;
        white-space: nowrap;
    }

    .mav__tab:hover:not(.mav__tab--active) {
        background: #f3f4f6;
        color: #374151;
    }

    .mav__tab--active {
        background: #111827;
        color: #ffffff;
        font-weight: 600;
    }

    /* Content area */
    .mav__content {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding-bottom: 4px;
    }

    .mav__section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .mav__section-card {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 18px;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        background: #ffffff;
    }

    .mav__section-title {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .mav__section-title-icon {
        font-size: 16px;
        font-weight: 700;
        color: #6b7280;
    }

    /* Fields */
    .mav__field {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .mav__field-label {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
    }

    .mav__field-hint {
        font-size: 12px;
        color: #9ca3af;
        margin: 0 0 4px;
    }

    .mav__field-hint--center {
        text-align: center;
        margin: -2px 0 0;
    }

    .mav__field-error {
        font-size: 12px;
        color: #b91c1c;
        margin: 0;
    }

    .mav__banner {
        padding: 10px 12px;
        border-radius: 8px;
        font-size: 13px;
        margin-bottom: 14px;
        border: 1px solid transparent;
    }

    .mav__banner--info {
        background: #eef2ff;
        color: #3730a3;
        border-color: #c7d2fe;
    }

    .mav__banner--warn {
        background: #fffbeb;
        color: #92400e;
        border-color: #fde68a;
    }

    .mav__banner--error {
        background: #fef2f2;
        color: #b91c1c;
        border-color: #fecaca;
    }

    .mav__row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .mav__input,
    .mav__select {
        height: 42px;
        padding: 0 12px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        font-size: 14px;
        color: #111827;
        background: #f9fafb;
        outline: none;
        transition: border-color 0.15s;
        width: 100%;
        box-sizing: border-box;
    }

    .mav__input:focus,
    .mav__select:focus {
        border-color: #6366f1;
        background: #ffffff;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.08);
    }

    .mav__select {
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%236b7280' fill='none' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-color: #f9fafb;
        padding-right: 32px;
    }

    /* Day checkboxes */
    .mav__days-row {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        align-items: center;
    }

    .mav__day {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #374151;
        cursor: pointer;
        user-select: none;
    }

    .mav__day--checked {
        color: #111827;
        font-weight: 500;
    }

    .mav__day-input {
        width: 16px;
        height: 16px;
        accent-color: #111827;
        cursor: pointer;
        margin: 0;
    }

    /* Checkbox row */
    .mav__checkbox-row {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #374151;
        cursor: pointer;
    }

    .mav__checkbox {
        width: 16px;
        height: 16px;
        accent-color: #111827;
    }

    /* Buttons */
    .mav__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 40px;
        padding: 0 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
        border: none;
    }

    .mav__btn--primary {
        background: #111827;
        color: #ffffff;
    }

    .mav__btn--primary:hover {
        background: #1f2937;
    }

    .mav__btn--primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .mav__btn--secondary {
        background: #ffffff;
        color: #374151;
        border: 1px solid #d1d5db;
    }

    .mav__btn--secondary:hover {
        background: #f9fafb;
    }

    .mav__btn--full {
        width: 100%;
    }

    .mav__btn-icon {
        font-size: 16px;
        font-weight: 700;
    }

    /* Footer */
    .mav__footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding-top: 20px;
        border-top: 1px solid #e5e7eb;
        margin-top: 20px;
    }

    /* Empty state */
    .mav__empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 24px 16px;
        border: 1px dashed #d1d5db;
        border-radius: 10px;
        text-align: center;
    }

    .mav__empty-icon {
        opacity: 0.4;
    }

    .mav__empty-text {
        font-size: 14px;
        font-weight: 500;
        color: #6b7280;
        margin: 0;
    }

    .mav__empty-hint {
        font-size: 12px;
        color: #9ca3af;
        margin: 0;
    }

    /* List items */
    .mav__list {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .mav__list-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 14px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
    }

    .mav__list-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .mav__list-date {
        font-size: 13px;
        font-weight: 600;
        color: #111827;
    }

    .mav__list-detail {
        font-size: 12px;
        color: #6b7280;
    }

    .mav__list-reason {
        font-size: 11px;
        color: #9ca3af;
        font-style: italic;
    }

    .mav__list-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        background: transparent;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s;
        padding: 0;
    }

    .mav__list-remove:hover {
        background: #fee2e2;
        border-color: #fca5a5;
    }

    /* Responsive */
    @media (max-width: 480px) {
        .mav__row {
            grid-template-columns: 1fr;
        }
        .mav__days-row {
            gap: 6px;
        }
        .mav__day {
            padding: 5px 10px;
            font-size: 12px;
        }
        .mav__tabs {
            flex-direction: column;
        }
    }
</style>
